// User Types
export interface User {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  photoURL?: string;
  profilePicture?: string;
  phoneNumber?: string;
  address?: string;
  bio?: string;
  specialization?: string;
  isVerified?: boolean;
  createdAt?: number;
  updatedAt?: number;
  isActive?: boolean;
  courses?: string[];
}

// Course Types
export interface Course {
  id: string;
  title: string;
  description?: string;
  imageUrl?: string;
  teacherId: string;
  teacherName: string;
  subject?: CourseSubject;
  level?: CourseLevel;
  price: number;
  duration?: number;
  rating?: number;
  studentsCount?: number;
  requirements?: string;
  objectives?: string;
  isPublished: boolean;
  category?: string;
  isFeatured?: boolean;
  enrolledCount?: number;
  lessonsCount?: number;
  enrolledStudents?: string[];
  teacherImage?: string;
  teacherTitle?: string;
  teacherBio?: string;
  teacherCoursesCount?: number;
  teacherStudentsCount?: number;
  teacherRating?: number;
  createdAt?: number;
  updatedAt?: number;
}

export enum CourseSubject {
  MATH = 'math',
  SCIENCE = 'science',
  ENGLISH = 'english',
  ARABIC = 'arabic',
  COMPUTER = 'computer',
  OTHER = 'other',
  PROGRAMMING = 'programming',
  DESIGN = 'design',
  BUSINESS = 'business',
  MARKETING = 'marketing',
  LANGUAGE = 'language',
  PHYSICS = 'physics',
  CHEMISTRY = 'chemistry',
  BIOLOGY = 'biology',
  HISTORY = 'history',
  GEOGRAPHY = 'geography',
  RELIGION = 'religion'
}

export enum CourseLevel {
  BEGINNER = 'beginner',
  INTERMEDIATE = 'intermediate',
  ADVANCED = 'advanced'
}

export enum UserRole {
  ADMIN = 'admin',
  TEACHER = 'teacher',
  STUDENT = 'student'
}

// Lesson Types
export interface Lesson {
  id: string;
  courseId: string;
  title: string;
  description?: string;
  videoUrl?: string;
  duration?: number;
  order: number;
  isFree?: boolean;
  isPublished: boolean;
  attachments?: Attachment[];
  createdAt: number;
  updatedAt: number;
}

export interface Attachment {
  id?: string;
  name: string;
  url: string;
  type?: string;
  size?: number;
  path?: string;
  uploadedAt?: number;
  createdAt?: number;
}

export interface VideoProgress {
  id?: string;
  userId: string;
  courseId: string;
  lessonId: string;
  progress: number;
  lastPosition?: number;
  duration?: number;
  isCompleted?: boolean;
  watchedSeconds?: number;
  completionPercentage?: number;
  lastWatched?: number;
  totalSeconds?: number;
  createdAt?: number;
  updatedAt?: number;
}

export interface StudentEnrollment {
  id?: string;
  userId: string;
  courseId: string;
  enrollmentDate: number;
  expiryDate?: number;
  isActive: boolean;
  progress?: number;
  lastAccessDate?: number;
  completedLessons?: string[];
  courseName: string;
  courseImage?: string;
  codeUsed: string;
  isPermanent: boolean;
  createdAt: number;
  updatedAt: number;
}

export interface CourseCode {
  id: string;
  code: string;
  courseId: string;
  isUsed: boolean;
  usedBy?: string;
  usedAt?: number | null;
  createdAt: number;
  createdBy?: string;
  expiryDate?: number | null;
  isExpired?: boolean;
}

export interface Assignment {
  id?: string;
  title: string;
  name?: string;
  description: string;
  courseId: string;
  lessonId?: string;
  dueDate?: number;
  points?: number;
  createdAt: number;
  createdBy: string;
  isPublished: boolean;
  isActive?: boolean;
}

export interface DashboardStats {
  totalStudents: number;
  totalCourses: number;
  totalLessons: number;
  totalRevenue: number;
  completedLessons: number;
  pendingAssignments: number;
  completedAssignments: number;
  
  // حقول إضافية للوحة معلومات الطالب
  enrolledCourses?: number;
  completedCourses?: number;
  inProgressCourses?: number;
  totalWatchTime?: number;
  upcomingAssignments?: number;
  upcomingExams?: number;
}

export interface ActivationCode {
  id?: string;
  code: string;
  isUsed: boolean;
  usedBy?: string;
  usedAt?: number;
  createdAt: number;
  expiryDate?: number;
  isActive?: boolean;
  usedCount?: number;
  maxUses?: number;
  courseId?: string;
  lessonId?: string;
  updatedAt?: number;
  createdBy?: string;
}

export interface StudentProgress {
  id?: string;
  userId: string;
  userName: string;
  userEmail: string;
  userPhoto?: string;
  courseId: string;
  courseName: string;
  enrollmentDate: number;
  lastAccessDate?: number;
  progress: number;
  completedLessons: number;
  totalLessons: number;
  watchTime: number;
  engagementScore?: number;
  notes?: string;
  createdAt: number;
  updatedAt: number;
}

export interface TeacherDashboardStats {
  totalStudents: number;
  activeStudents: number;
  totalCourses: number;
  totalLessons: number;
  totalRevenue: number;
  totalWatchTime: number;
  averageProgress: number;
  averageEngagement: number;
  studentsPerCourse: { [courseId: string]: number };
  progressPerCourse: { [courseId: string]: number };
  recentEnrollments: StudentEnrollment[];
  topPerformingStudents: StudentProgress[];
  lowEngagementStudents: StudentProgress[];
}

export interface LessonEngagement {
  lessonId: string;
  lessonTitle: string;
  courseId: string;
  watchCount: number;
  averageWatchTime: number;
  completionRate: number;
  averageProgress: number;
}

export interface StudentDetail {
  user: User;
  enrollments: StudentEnrollment[];
  progress: { [courseId: string]: StudentProgress };
  videoProgress: { [lessonId: string]: VideoProgress };
  totalWatchTime: number;
  averageProgress: number;
  lastActivity: number;
  notes?: string;
  engagementScore: number;
}
